import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/auth/AuthProvider';
import type { JobPosting } from '@/modules/job/services/job.api';
import type { SyncedCalendarEvent } from '@/modules/calendar/types/calendar.types';
import type { TaskItem } from '@/modules/shared/types/yc-demo';
import {
    composeActivityFeed,
    fetchActiveJobs,
    fetchPendingTasks,
    fetchTodaysEvents,
    getLastActionAgoMs,
    type ActivityFeedEvent,
} from './services/dashboard-data';

export interface DashboardData {
    jobs: JobPosting[];
    tasks: TaskItem[];
    events: SyncedCalendarEvent[];
    activity: ActivityFeedEvent[];
    lastActionAgoMs: number;
}

export type DashboardLoadState =
    | { phase: 'loading' }
    | { phase: 'error'; message: string }
    | { phase: 'ready'; data: DashboardData };

export interface UseDashboardDataResult {
    state: DashboardLoadState;
    userName: string;
    refresh(): void;
}

export function useDashboardData(): UseDashboardDataResult {
    const { user } = useAuth();
    const [state, setState] = useState<DashboardLoadState>({ phase: 'loading' });
    const [refreshKey, setRefreshKey] = useState(0);

    const userName = useMemo(() => {
        if (!user) return 'there';
        const first = user.firstName?.trim();
        const last = user.lastName?.trim();
        if (first && last) return `${first} ${last}`;
        if (first) return first;
        return user.email?.split('@')[0] ?? 'there';
    }, [user]);

    useEffect(() => {
        let cancelled = false;
        async function load() {
            setState({ phase: 'loading' });
            try {
                const jobs = await fetchActiveJobs();
                const [tasks, events] = await Promise.all([
                    fetchPendingTasks(jobs),
                    fetchTodaysEvents(),
                ]);
                if (cancelled) return;
                setState({
                    phase: 'ready',
                    data: {
                        jobs,
                        tasks,
                        events,
                        activity: composeActivityFeed(jobs, tasks),
                        lastActionAgoMs: getLastActionAgoMs(jobs, tasks),
                    },
                });
            } catch (err) {
                if (cancelled) return;
                console.error('Dashboard load failed', err);
                setState({
                    phase: 'error',
                    message: "Couldn't reach Lantern's agent. The dashboard will retry once you reconnect.",
                });
            }
        }
        load();
        return () => {
            cancelled = true;
        };
    }, [refreshKey]);

    return {
        state,
        userName,
        refresh: () => setRefreshKey((k) => k + 1),
    };
}

export function buildTaskRoute(task: TaskItem) {
    return task.kind === 'cv_review'
        ? `/v2/reports/cv-screening/${task.applicationId}?taskId=${task._id}`
        : `/v2/reports/ai-interview/${task.applicationId}?taskId=${task._id}`;
}
